import './App.css';
import React, { Suspense, lazy, useEffect } from 'react';
import { Layout } from 'antd';
import {
  FontColorsOutlined,
  SyncOutlined,
  SearchOutlined,
  RocketOutlined,
} from '@ant-design/icons';
import { Route, Redirect, Switch } from 'react-router-dom';
import styled from 'styled-components';

import { useAuth0 } from '@auth0/auth0-react';
import { storage } from '../utils/localStorage';
import { setThirdPartyUser } from '../utils/auth';

import GlobalFooter from 'components/GlobalFooter';
import { SideNav } from 'components/SideNav';
import { Spinner } from 'components/Spinner';
import { hasUserRole } from 'utils/auth';

const AssetManager = lazy(() => import('./routes/AssetManager'));
const Equipment = lazy(() => import('./routes/Equipment'));
const IngestionTable = lazy(() => import('./routes/Ingestion'));
const LogFinder = lazy(() => import('./routes/LogFinder'));

const { Content } = Layout;

const StyledLayout = styled(Layout)`
  overflow-x: auto !important;
`;

export default function App() {
  const { user, getIdTokenClaims } = useAuth0();

  useEffect(() => {
    const groups = user['https://skyspecs.com/jwt/ohi']?.groups;
    if (groups) {
      storage.setItem('ohi:groups', groups);
    }

    (async () => {
      try {
        const idToken = await getIdTokenClaims();
        setThirdPartyUser(idToken);
      } catch (e) {
        console.error(e);
      }
    })();
  }, [user, getIdTokenClaims]);

  const createMenuItems = () =>
    [
      hasUserRole('ops', 'analyst-admin') && {
        title: 'Log Finder',
        icon: SearchOutlined,
        link: '/logfinder',
        id: 'sidebar-logfinder-button',
      },
      {
        title: 'Asset Explorer',
        icon: FontColorsOutlined,
        link: '/asset-manager',
        id: 'sidebar-asset-manager-button',
      },
      hasUserRole('ops', 'analyst-admin') && {
        title: 'Ingestion',
        icon: SyncOutlined,
        link: '/ingestion',
        id: 'sidebar-ingestion-button',
      },
      hasUserRole('ops', 'auton') && {
        title: 'Equipment',
        icon: RocketOutlined,
        link: '/equipment',
        id: 'sidebar-equipment-button',
      },
    ].filter(Boolean);

  const menuItems = createMenuItems();

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <SideNav tabs={menuItems} />

      <StyledLayout>
        <Content>
          <Suspense fallback={<Spinner />}>
            <Switch>
              <Route
                exact
                path="/"
                render={props => {
                  // handle old "hash" routes with a simple redirect
                  const { hash } = props.location;
                  return hash ? (
                    <Redirect to={hash.replace('#', '')} />
                  ) : (
                    <Redirect to="/logfinder" />
                  );
                }}
              />
              <Route path="/equipment" component={Equipment} key="equipment" />
              <Route path="/ingestion" component={IngestionTable} key="ingestion" />
              <Route path="/logfinder/:droneId?/:logId?" component={LogFinder} key="logfinder" />
              <Route
                path={`/asset-manager/(customer)?/:customerId?`}
                component={AssetManager}
                key="asset-manager"
              />
              <Redirect to="/logfinder" />
            </Switch>
          </Suspense>
        </Content>

        <GlobalFooter />
      </StyledLayout>
    </Layout>
  );
}
